import { useState, useRef } from "react"
import Card from "~/components/Card"
import { IOmniAuthProvider } from "~/types/recruiter"
import AuthenticityTokenInput from "../AuthenticityTokenInput"

export default function OmniAuthProviderSelector({
  providers,
  relayState
}: {
  providers: IOmniAuthProvider[]
  relayState?: string
}) {
  const [selectedProvider, setSelectedProvider] =
    useState<IOmniAuthProvider | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const handleProviderSelect = (provider: IOmniAuthProvider) => {
    setSelectedProvider(provider)
    setIsDropdownOpen(false)
    submitForm(provider)
  }

  const submitForm = (provider: IOmniAuthProvider) => {
    if (!formRef.current) return

    formRef.current.action = `/recruiter/auth/saml/${provider.slug}`
    formRef.current.submit()
  }

  return (
    <Card dataTestId="provider-selector-card">
      <div className="flex flex-col gap-2">
        <label className="text-neutral-700 font-thin text-xs">
          Vælg din region
        </label>

        <div className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl hover:border-primary-300 transition-colors"
            data-testid="region-dropdown-button"
          >
            <span
              className={
                selectedProvider ? "text-black" : "text-neutral-500 text-sm"
              }
            >
              {selectedProvider?.name || "Vælg din region..."}
            </span>
            <svg
              className={`w-5 h-5 text-black transition-transform ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isDropdownOpen && providers.length > 0 && (
            <div
              className="absolute top-full left-0 right-0 mt-2 bg-white border border-primary-200 rounded-xl shadow-lg z-10 overflow-hidden"
              data-testid="region-dropdown-list"
            >
              {providers.map((provider) => (
                <button
                  key={provider.slug}
                  type="button"
                  onClick={() => handleProviderSelect(provider)}
                  className="w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors border-b border-primary-100 last:border-b-0 text-sm"
                  data-testid={`region-option-${provider.slug}`}
                >
                  <span className="text-black">{provider.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <form
          ref={formRef}
          method="post"
          className="hidden"
          data-testid="login-form"
        >
          <AuthenticityTokenInput />
          {Boolean(relayState) && (
            <input type="hidden" name="RelayState" value={relayState} />
          )}
        </form>
      </div>
    </Card>
  )
}
